import React, { useState } from 'react';

const ShareToClientModal = ({ study, isOpen, onClose, onSuccess }) => {
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleShare = async () => {
    // Validation
    if (!clientName.trim()) {
      setError('Veuillez saisir le nom du client');
      return;
    }

    if (!clientEmail.trim()) {
      setError('Veuillez saisir une adresse email');
      return;
    }

    if (!validateEmail(clientEmail)) {
      setError('Adresse email invalide');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await fetch(`${API_URL}/api/analysis/${study.analysis_id}/share-client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_email: clientEmail.trim(),
          client_name: clientName.trim(),
          message: message.trim() || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        onSuccess && onSuccess(data);
        onClose();
        // Reset form
        setClientEmail('');
        setClientName('');
        setMessage('');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors du partage de l\'étude');
      }
    } catch (err) {
      console.error('Share error:', err);
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            color: 'var(--pde-anthracite)'
          }}>📧 Partager par email</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--pde-gris-acier)',
              padding: '0.25rem 0.5rem'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            marginBottom: '0.75rem',
            padding: '0.75rem',
            backgroundColor: '#f0f9ff',
            borderRadius: '6px',
            border: '1px solid #bae6fd'
          }}>
            <span style={{ 
              fontSize: '0.875rem',
              color: '#0369a1',
              fontWeight: '500'
            }}>
              Étude :
            </span>
            <strong style={{ 
              fontSize: '0.875rem',
              color: '#0c4a6e'
            }}>
              {study?.title || study?.client_name || 'Sans nom'}
            </strong>
          </div>
        </div>

        {/* Nom du client */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '600',
            color: 'var(--pde-anthracite)'
          }}>
            Nom du client *
          </label>
          <input
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Ex: Société ABC, M. Dupont..."
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
            data-testid="client-name-input"
          />
          <p style={{ 
            margin: '0.25rem 0 0 0', 
            fontSize: '0.75rem', 
            color: 'var(--pde-gris-acier)' 
          }}>
            Ce nom sera affiché dans l'en-tête de l'étude pour le client
          </p>
        </div>

        {/* Email du client */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '600',
            color: 'var(--pde-anthracite)'
          }}>
            Email du client *
          </label>
          <input
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            placeholder="client@example.com"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '1rem',
              boxSizing: 'border-box'
            }}
            data-testid="client-email-input"
          />
        </div>

        {/* Message optionnel */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '600',
            color: 'var(--pde-anthracite)'
          }}>
            Message personnalisé (optionnel)
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ajoutez un message pour votre client..."
            rows={3}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '1rem',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
            data-testid="message-input"
          />
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#991b1b',
            fontSize: '0.875rem'
          }}>
            {error}
          </div>
        )}

        {/* Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              color: '#991b1b',
              backgroundColor: '#fee2e2',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              minWidth: '120px'
            }}
            data-testid="cancel-share-btn"
          >
            Annuler
          </button>
          <button
            onClick={handleShare}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              color: '#166534',
              backgroundColor: '#dcfce7',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              minWidth: '120px'
            }}
            data-testid="confirm-share-btn"
          >
            {loading ? 'Envoi...' : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareToClientModal;
